// Knowledge base categories and data for the AI health coach
export const knowledgeBase = {
  recovery: {
    basics: {
      definition: "Recovery is the body's process of adapting to training stress and returning to a state of readiness.",
      importance: "Proper recovery is essential for performance improvements and injury prevention.",
      metrics: ["Heart Rate Variability (HRV)", "Resting Heart Rate", "Sleep Quality", "Perceived Fatigue"]
    },
    recommendations: {
      low: [
        "Focus on light activities like walking or gentle stretching",
        "Prioritize sleep and stress management",
        "Consider active recovery techniques like foam rolling",
        "Stay hydrated and maintain good nutrition"
      ],
      moderate: [
        "Moderate intensity activities are appropriate",
        "Listen to your body and adjust intensity as needed",
        "Include mobility work",
        "Maintain good sleep hygiene"
      ],
      high: [
        "Your body is well-recovered for high-intensity training",
        "Optimal time for challenging workouts",
        "Still maintain good recovery practices",
        "Monitor for signs of overtraining"
      ]
    },
    tools: {
      foamRoller: {
        name: "Foam Rolling",
        duration: "10 minutes",
        intensity: "Low",
        targetAreas: ["Quads", "IT Band", "Calves"],
        benefits: "Improves circulation and reduces muscle tension"
      }
    },
    scienceExplanation: "Recovery is influenced by both parasympathetic and sympathetic nervous system activity. Higher HRV typically indicates better recovery status and autonomic nervous system balance."
  },

  sleep: {
    stages: {
      light: {
        name: "Light Sleep",
        percentage: "50-60%",
        description: "Initial sleep stage where body temperature drops and heart rate slows",
        importance: "Prepares body for deeper sleep stages",
        features: ["Easily awakened", "Muscle relaxation begins", "Slower brain waves"]
      },
      deep: {
        name: "Deep Sleep",
        percentage: "20-25%",
        description: "Critical for physical recovery, growth hormone release, and immune function",
        importance: "Primary stage for physical restoration",
        features: ["Muscle repair", "Immune system strengthening", "Growth hormone release"]
      },
      rem: {
        name: "REM Sleep",
        percentage: "15-20%",
        description: "Important for cognitive recovery, memory consolidation, and emotional regulation",
        importance: "Essential for mental recovery and learning",
        features: ["Dream state", "Memory consolidation", "Emotional processing"]
      }
    },
    optimization: {
      environment: [
        "Keep room temperature between 65-68°F (18-20°C)",
        "Ensure complete darkness with blackout curtains",
        "Minimize noise disruption",
        "Use breathable bedding materials"
      ],
      routine: [
        "Maintain consistent sleep/wake times",
        "Avoid blue light exposure 1-2 hours before bed",
        "Practice relaxation techniques",
        "Limit caffeine after 2 PM"
      ],
      tools: {
        tracking: ["Sleep tracking devices", "Sleep diary"],
        features: ["Sleep duration", "Sleep quality", "Sleep cycle analysis"]
      }
    },
    scienceExplanation: "During sleep, the brain cycles through different stages, each serving unique recovery functions. The glymphatic system becomes more active, clearing metabolic waste products from the brain."
  },

  training: {
    exercises: {
      strength: [
        {
          name: "Squat",
          muscleGroup: "Quadriceps, Glutes",
          equipment: "Barbell",
          intensity: "High",
          progression: "Increase weight",
          type: "Compound"
        },
        {
          name: "Deadlift",
          muscleGroup: "Hamstrings, Back",
          equipment: "Barbell",
          intensity: "High",
          progression: "Add weights",
          type: "Compound"
        }
      ],
      cardio: [
        {
          name: "Running",
          type: "Steady State",
          intensity: "Moderate",
          progression: "Increase distance",
          benefits: ["Cardiovascular endurance", "Calorie burn"]
        },
        {
          name: "HIIT",
          type: "Interval",
          intensity: "High",
          progression: "Increase intervals",
          benefits: ["Metabolic boost", "Time efficient"]
        }
      ],
      flexibility: [
        {
          name: "Hamstring Stretch",
          target: "Hamstrings",
          intensity: "Low",
          progression: "Hold longer",
          type: "Static"
        },
        {
          name: "Butterfly Stretch",
          target: "Hip Adductors",
          intensity: "Low",
          progression: "Increase duration",
          type: "Static"
        }
      ]
    },
    recommendations: {
      low: [
        "Focus on technique and form",
        "Start with bodyweight exercises",
        "Gradually increase intensity",
        "Listen to your body"
      ],
      moderate: [
        "Progressive overload",
        "Mix strength and cardio",
        "Include recovery days",
        "Track progress"
      ],
      high: [
        "Challenge yourself with complex movements",
        "Incorporate periodization",
        "Focus on specific goals",
        "Monitor fatigue levels"
      ]
    }
  },

  nutrition: {
    macronutrients: {
      protein: {
        role: "Essential for muscle repair and growth",
        timing: "Distribute throughout the day, especially post-workout",
        sources: ["Lean meats", "Fish", "Eggs", "Legumes", "Dairy"],
        recommendations: {
          chicken: {
            protein: "31g",
            carbs: "0g",
            fat: "3.6g",
            calories: "165 kcal",
            perServing: "100g"
          }
        }
      },
      carbohydrates: {
        role: "Primary energy source for high-intensity activities",
        timing: "Higher intake around workouts",
        sources: ["Whole grains", "Fruits", "Vegetables", "Legumes"],
        recommendations: {
          quinoa: {
            protein: "4g",
            carbs: "21g",
            fat: "1.9g",
            calories: "120 kcal",
            perServing: "100g"
          }
        }
      },
      fats: {
        role: "Hormone production, nutrient absorption, energy for low-intensity activities",
        timing: "Moderate intake throughout the day",
        sources: ["Avocados", "Nuts", "Seeds", "Olive oil"]
      }
    },
    hydration: {
      guidelines: "Minimum 3L daily, more during intense training",
      timing: "Regular intake throughout the day",
      signs: {
        dehydration: ["Dark urine", "Thirst", "Fatigue", "Decreased performance"],
        optimal: ["Light yellow urine", "Regular output", "Maintained performance"]
      }
    },
    supplements: {
      recovery: [
        {
          name: "Magnesium",
          dosage: "200-400mg",
          timing: "Evening",
          benefits: ["Muscle relaxation", "Sleep quality", "Recovery"]
        },
        {
          name: "Omega-3",
          dosage: "1-3g",
          timing: "With meals",
          benefits: ["Inflammation reduction", "Joint health"]
        }
      ],
      performance: [
        {
          name: "Creatine",
          dosage: "5g daily",
          timing: "Any time",
          benefits: ["Strength", "Power output", "Recovery"]
        },
        {
          name: "Beta-Alanine",
          dosage: "3-5g daily",
          timing: "With meals",
          benefits: ["Endurance", "Performance"]
        }
      ]
    }
  }
};

export const personalityTraits = {
  communication: {
    style: "Professional yet friendly",
    tone: "Encouraging and supportive",
    approach: "Evidence-based with practical applications"
  },
  responses: {
    structure: ["Acknowledge", "Educate", "Recommend", "Encourage"],
    characteristics: [
      "Clear and concise",
      "Scientifically accurate",
      "Personalized to user metrics",
      "Action-oriented"
    ]
  }
};